import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { IconMenu, IconX } from "./Icons";

const navLinks = [
  { name: "Veterinary Training", path: "/category/veterinary-training" },
  { name: "Surgical Skills", path: "/category/surgical-skills" },
  { name: "Animal Behaviour", path: "/category/animal-behaviour" },
  { name: "Anatomical Models", path: "/anatomical-models" },
  { name: "About Haptone", path: "/about" },
  { name: "Contact Us", path: "/contact" },
];

const externalLinks = [
  { name: "Trainings", href: "https://shop.haptone.com" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg"
            : "bg-primary/80 backdrop-blur-sm"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link to="/" className="flex-shrink-0" aria-label="Haptone Home">
              <img
                src="/logo.png"
                alt="Haptone"
                className={`h-9 lg:h-11 w-auto transition-all duration-300 ${
                  scrolled ? "" : "brightness-0 invert"
                }`}
                width={120}
                height={44}
              />
            </Link>

            <div className="hidden xl:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname.startsWith(link.path)
                      ? scrolled
                        ? "text-accent bg-accent/10"
                        : "text-accent-light bg-white/10"
                      : scrolled
                      ? "text-primary hover:text-accent hover:bg-accent/5"
                      : "text-white/90 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              {externalLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 px-4 py-2 rounded-lg text-sm font-semibold bg-accent text-white hover:bg-accent-light transition-all duration-200"
                >
                  {link.name}
                </a>
              ))}
            </div>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`xl:hidden p-2 rounded-lg transition-colors ${
                scrolled
                  ? "text-primary hover:bg-gray-100"
                  : "text-white hover:bg-white/10"
              }`}
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
            >
              {isOpen ? <IconX size={24} /> : <IconMenu size={24} />}
            </button>
          </div>
        </nav>
      </header>

      {createPortal(
        <div
          className={`xl:hidden fixed inset-0 z-[9999] transition-transform duration-300 ease-in-out ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
          style={{ backgroundColor: "#0a1628" }}
        >
          <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
            <Link to="/" onClick={() => setIsOpen(false)}>
              <img
                src="/logo.png"
                alt="Haptone"
                className="h-9 w-auto brightness-0 invert"
                width={120}
                height={44}
              />
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
              aria-label="Close menu"
            >
              <IconX size={24} />
            </button>
          </div>

          <div className="flex flex-col p-6 gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 ${
                  location.pathname.startsWith(link.path)
                    ? "text-accent-light bg-white/10"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
              >
                {link.name}
              </Link>
            ))}
            {externalLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 px-4 py-3 rounded-xl text-base font-semibold text-center bg-accent text-white hover:bg-accent-light transition-all duration-200"
              >
                {link.name}
              </a>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
